import React, { useState, useEffect } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MetodoCruzDia = () => {
  const [betDate, setBetDate] = useState<string>("");
  const [showResults, setShowResults] = useState(false);
  const [calculationSteps, setCalculationSteps] = useState<string[]>([]);
  const [finalNumbers, setFinalNumbers] = useState<number[]>([0, 7, 0, 3]);
  const [combinations, setCombinations] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<{
    milhares: string[];
    centenas: string[];
    grupos: string[];
  }>({ milhares: [], centenas: [], grupos: [] });

  // Definir a data atual como padrão
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setBetDate(formattedDate);
    
    // Calcular automaticamente com a data atual
    setTimeout(() => {
      calculateCruzDoDia(formattedDate);
    }, 500);
  }, []);

  const calculateCruzDoDia = (dateValue?: string) => {
    const dateInput = dateValue || betDate;
    if (!dateInput) {
      alert('Por favor, selecione uma data!');
      return;
    }

    const date = new Date(dateInput + 'T00:00:00');
    const day = date.getDate();

    // Calcular os 4 passos
    const step1 = day + 3;
    const step2 = step1 + 3;
    const step3 = step2 + 3;
    const step4 = step3 + 3;

    // Extrair as dezenas (2 dígitos) de cada resultado
    const dezena1 = step1.toString().padStart(2, '0');
    const dezena2 = step2.toString().padStart(2, '0');
    const dezena3 = step3.toString().padStart(2, '0');
    const dezena4 = step4.toString().padStart(2, '0');

    // Também extrair unidades para combinações extras
    const num1 = step1 % 10;
    const num2 = step2 % 10;
    const num3 = step3 % 10;
    const num4 = step4 % 10;

    // Preparar os passos para exibição
    const steps = [
      `Dia da aposta: ${day}`,
      `${day} + 3 = ${step1} → Dezena: ${dezena1}`,
      `${step1} + 3 = ${step2} → Dezena: ${dezena2}`,
      `${step2} + 3 = ${step3} → Dezena: ${dezena3}`,
      `${step3} + 3 = ${step4} → Dezena: ${dezena4}`,
      `Unidades: ${num1}, ${num2}, ${num3}, ${num4}`
    ];

    setCalculationSteps(steps);
    setFinalNumbers([parseInt(dezena1), parseInt(dezena2), parseInt(dezena3), parseInt(dezena4)]);
    generateCombinations([parseInt(dezena1), parseInt(dezena2), parseInt(dezena3), parseInt(dezena4)], [num1, num2, num3, num4], day);
    setShowResults(true);
  };

  const generateCombinations = (dezenas: number[], unidades: number[], day: number) => {
    const combos: string[] = [];

    // Multiplicações entre pares de dezenas
    for (let i = 0; i < dezenas.length; i++) {
      for (let j = i + 1; j < dezenas.length; j++) {
        const product = dezenas[i] * dezenas[j];
        if (product > 0) {
          combos.push(`${dezenas[i].toString().padStart(2, '0')} × ${dezenas[j].toString().padStart(2, '0')} = ${product.toString().padStart(4, '0')}`);
        }
      }
    }

    // Combinações especiais de dezenas
    const specialCombos = [
      `${dezenas[0].toString().padStart(2, '0')}${dezenas[1].toString().padStart(2, '0')}`,
      `${dezenas[1].toString().padStart(2, '0')}${dezenas[2].toString().padStart(2, '0')}`,
      `${dezenas[2].toString().padStart(2, '0')}${dezenas[3].toString().padStart(2, '0')}`,
      `${dezenas[0].toString().padStart(2, '0')}${dezenas[3].toString().padStart(2, '0')}`
    ];

    specialCombos.forEach(combo => {
      if (combo !== '0000') {
        combos.push(combo);
      }
    });

    setCombinations(combos);

    // Gerar sugestões criativas
    generateSuggestions(dezenas, unidades, day);
  };

  const generateSuggestions = (dezenas: number[], unidades: number[], day: number) => {
    // MILHARES - Combinações matemáticas criativas
    const milhares = [
      // Sequências baseadas nas dezenas
      `${dezenas[0].toString().padStart(2, '0')}${dezenas[1].toString().padStart(2, '0')}`,
      `${dezenas[2].toString().padStart(2, '0')}${dezenas[3].toString().padStart(2, '0')}`,
      `${dezenas[0].toString().padStart(2, '0')}${dezenas[3].toString().padStart(2, '0')}`,
      // Combinações invertidas
      `${dezenas[1].toString().padStart(2, '0')}${dezenas[0].toString().padStart(2, '0')}`,
      `${dezenas[3].toString().padStart(2, '0')}${dezenas[2].toString().padStart(2, '0')}`,
      // Soma do dia com dezenas
      `${(day + dezenas[0]).toString().padStart(2, '0')}${(day + dezenas[1]).toString().padStart(2, '0')}`,
      // Multiplicação criativa
      `${((dezenas[0] * 2) % 100).toString().padStart(2, '0')}${((dezenas[1] * 3) % 100).toString().padStart(2, '0')}`,
      `${((day * dezenas[0]) % 100).toString().padStart(2, '0')}${((day * dezenas[1]) % 100).toString().padStart(2, '0')}`
    ];

    // CENTENAS - Base nas unidades e dezenas
    const centenas = [
      // Centenas das dezenas principais
      dezenas[0].toString().padStart(3, '0'),
      dezenas[1].toString().padStart(3, '0'),
      dezenas[2].toString().padStart(3, '0'),
      dezenas[3].toString().padStart(3, '0'),
      // Combinações com dia
      `${day.toString().padStart(2, '0')}${unidades[0]}`,
      `${day.toString().padStart(2, '0')}${unidades[1]}`,
      // Sequências matemáticas
      `${unidades[0]}${unidades[1]}${unidades[2]}`,
      `${unidades[1]}${unidades[2]}${unidades[3]}`,
      `${unidades[0]}${unidades[2]}${unidades[3]}`,
      // Soma e multiplicação das unidades
      `${((unidades[0] + unidades[1]) % 10)}${((unidades[2] + unidades[3]) % 10)}${day % 10}`,
      `${((unidades[0] * unidades[1]) % 10)}${((unidades[2] * unidades[3]) % 10)}${((day * 2) % 10)}`
    ];

    // GRUPOS DO BICHO (01-25)
    const grupos = [
      // Grupos baseados nas dezenas (dividido por 4 + 1)
      ((dezenas[0] % 25) + 1).toString().padStart(2, '0'),
      ((dezenas[1] % 25) + 1).toString().padStart(2, '0'),
      ((dezenas[2] % 25) + 1).toString().padStart(2, '0'),
      ((dezenas[3] % 25) + 1).toString().padStart(2, '0'),
      // Grupos baseados no dia
      ((day % 25) + 1).toString().padStart(2, '0'),
      // Grupos baseados em operações
      (((dezenas[0] + dezenas[1]) % 25) + 1).toString().padStart(2, '0'),
      (((dezenas[2] + dezenas[3]) % 25) + 1).toString().padStart(2, '0'),
      (((day + dezenas[0]) % 25) + 1).toString().padStart(2, '0'),
      // Grupos especiais matematicamente derivados
      (((unidades[0] + unidades[1] + unidades[2] + unidades[3]) % 25) + 1).toString().padStart(2, '0'),
      (((day * 3) % 25) + 1).toString().padStart(2, '0')
    ];

    // Remover duplicatas e garantir formato correto
    setSuggestions({
      milhares: [...new Set(milhares)].slice(0, 8),
      centenas: [...new Set(centenas)].slice(0, 10),
      grupos: [...new Set(grupos)].slice(0, 8)
    });
  };

  return (
    <div className="w-full max-w-full overflow-x-auto">
      <PageInstructions
        title="📖 Como usar o Método da Cruz do Dia"
        steps={[
          "A data de hoje já vem preenchida automaticamente (ou digite outra data)",
          "O sistema calcula a 'cruz' baseada no dia, mês e ano",
          "Clique em 'Calcular Cruz' para gerar os números",
          "Veja os 4 números da cruz e os grupos correspondentes",
          "Cada número da cruz representa uma direção (cima, baixo, esquerda, direita)",
          "Use esses números para montar suas apostas do dia"
        ]}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-8 rounded-t-xl text-center">
        <h1 className="text-4xl font-bold mb-2 drop-shadow-lg">🎯 Método da Cruz do Dia</h1>
        <p className="text-lg opacity-90">Estratégia para o Jogo do Bicho baseada na data da aposta</p>
      </div>

      <Card className="rounded-t-none border-t-0">
        <CardContent className="p-8">
          {/* Input Section */}
          <div className="bg-slate-50 p-8 rounded-xl mb-8 border-2 border-slate-200">
            <div className="space-y-4">
              <div>
                <Label htmlFor="betDate" className="text-lg font-bold text-gray-700 mb-2 block">
                  📅 Selecione a data da aposta:
                </Label>
                <Input
                  type="date"
                  id="betDate"
                  value={betDate}
                  onChange={(e) => setBetDate(e.target.value)}
                  className="text-lg p-4 border-2 border-gray-300 focus:border-blue-500 transition-all duration-300"
                />
              </div>
              <Button
                onClick={() => calculateCruzDoDia()}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 sm:px-10 py-3 sm:py-4 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                🔮 Calcular Cruz do Dia
              </Button>
            </div>
          </div>

          {/* Results */}
          {showResults && (
            <div className="space-y-6 animate-in fade-in-50 slide-in-from-bottom-5 duration-500">
              <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2">
                📊 Resultados do Método da Cruz do Dia
              </h3>

              {/* Calculation Steps */}
              <div className="bg-slate-50 p-6 rounded-xl">
                <h4 className="text-lg font-bold mb-4">📋 Passos do Cálculo:</h4>
                <div className="space-y-2">
                  {calculationSteps.map((step, index) => (
                    <div
                      key={index}
                      className={`font-mono text-lg ${
                        index === calculationSteps.length - 1
                          ? 'font-bold text-blue-600 mt-4'
                          : 'text-gray-600'
                      }`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div>

              {/* Final Numbers */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {finalNumbers.map((num, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-red-500 to-orange-500 text-white p-6 rounded-xl text-center text-4xl font-bold shadow-lg"
                  >
                    {num}
                  </div>
                ))}
              </div>

              {/* Combinations */}
              <div className="bg-cyan-50 p-4 sm:p-6 rounded-xl">
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-4">🎲 Combinações Sugeridas (Multiplicação):</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {combinations.map((combo, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-lg text-center font-bold text-gray-800 border-2 border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      {combo}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sugestões de Apostas */}
              <div className="grid md:grid-cols-3 gap-6">
                {/* Milhares */}
                <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200">
                  <h4 className="text-lg font-bold text-green-700 mb-4">💰 Milhares Sugeridos:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestions.milhares.map((milhar, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded-lg text-center font-bold text-green-800 border border-green-300"
                      >
                        {milhar}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Centenas */}
                <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                  <h4 className="text-lg font-bold text-blue-700 mb-4">🎯 Centenas Sugeridas:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestions.centenas.map((centena, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded-lg text-center font-bold text-blue-800 border border-blue-300"
                      >
                        {centena}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupos do Bicho */}
                <div className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200">
                  <h4 className="text-lg font-bold text-purple-700 mb-4">🦁 Grupos do Bicho:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestions.grupos.map((grupo, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded-lg text-center font-bold text-purple-800 border border-purple-300"
                      >
                        {grupo}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Explanation */}
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                <h4 className="text-lg font-bold text-yellow-700 mb-3">💡 Como funciona:</h4>
                <p className="text-gray-700 leading-relaxed">
                  O Método da Cruz do Dia pega o dia da aposta e soma 3 quatro vezes consecutivas. As dezenas resultantes (não apenas unidades) são os números base. O sistema gera sugestões matemáticas criativas para milhares, centenas e grupos do bicho baseadas nessas dezenas e no dia da aposta.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MetodoCruzDia;